import { useLayoutEffect } from "@react-aria/utils"
import PropTypes from "prop-types"
import { createContext, useContext, useEffect, useRef, useState } from "react"
import { useLocation, useNavigationType } from "react-router-dom"

const ScrollTrackerContext = createContext(null)

export const useScrollTracker = () => useContext(ScrollTrackerContext)

export const ScrollTrackerProvider = ({ children }) => {
  const [active, setActive] = useState(false)
  const [scrollMap, setScrollMap] = useState({})
  const scrollPos = useRef(0)
  const rafRef = useRef()
  const prevLocation = useRef()
  const action = useNavigationType()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      if (!rafRef.current) {
        rafRef.current = window.requestAnimationFrame(() => {
          scrollPos.current = window.scrollY
          rafRef.current = null
        })
      }
    }

    window.addEventListener("scroll", handleScroll)

    return () => {
      rafRef.current && window.cancelAnimationFrame(rafRef.current)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"

      return () => (window.history.scrollRestoration = "auto")
    }
  }, [])

  useLayoutEffect(() => {
    if (
      active &&
      action === "PUSH" &&
      prevLocation.current &&
      prevLocation.current.pathname
    ) {
      setScrollMap({
        ...scrollMap,
        [prevLocation.current.pathname]: scrollPos,
      })
    }
  }, [action, active])

  useLayoutEffect(() => {
    if (
      action === "PUSH" &&
      prevLocation.current &&
      prevLocation.current.hash === location.hash
    ) {
      window.scrollTo(0, 0)
    }
    prevLocation.current = location
  }, [action, location])

  return (
    <ScrollTrackerContext.Provider
      value={{
        active,
        scrollMap,
        scrollPos: scrollPos.current,
        setActive,
        setScrollMap,
      }}
    >
      {children}
    </ScrollTrackerContext.Provider>
  )
}

ScrollTrackerProvider.propTypes = {
  children: PropTypes.node,
}

export default ScrollTrackerProvider
